import { Body1, Body1Strong, Button, Checkbox, Spinner } from '@fluentui/react-components'
import { AddRegular } from '@fluentui/react-icons'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BraveUploadContent } from '../../components/BraveUploadContent/BraveUploadContent'
import { Dropzone } from '../../components/Dropzone/Dropzone'
import { FileCard } from '../../components/FileCard/FileCard'
import { FileUploadMenu } from '../../components/FileUploadMenu/FileUploadMenu'
import { HierarchyViewPills } from '../../components/HierarchyViewPills/HierarchyViewPills'
import { Overlay } from '../../components/Overlay/Overlay'
import { PageHeader } from '../../components/PageHeader/PageHeader'
import { UploadingFilesList } from '../../components/UploadingFilesList/UploadingFilesList.tsx'
import { WORKING_DOCUMENT_ID } from '../../constants/files.ts'
import {
  useGetFilePool,
  useGetWorkingDocumentHierarchicalStructure,
  useSelectAll,
} from '../../hooks'
import { useSelectedContext } from '../../hooks/useSelectedContext'
import type { HierarchyViewMode } from '../../utils/filter-section-tree'
import { useStyles } from './Files.style.tsx'

const Files = () => {
  const { data: files, isLoading, isError } = useGetFilePool()
  const {
    data: workingDocumentHierarchicalStructure,
    isLoading: isWorkingDocumentLoading,
    isError: isWorkingDocumentError,
  } = useGetWorkingDocumentHierarchicalStructure()
  const [isBraveOverlayOpen, setIsBraveOverlayOpen] = useState(false)
  const [expandedFileId, setExpandedFileId] = useState<string | null>(null)
  const [hierarchyViewMode, setHierarchyViewMode] = useState<HierarchyViewMode>('all')
  const classes = useStyles()
  const { t } = useTranslation()
  const { getFileSelection, updateFileSelection } = useSelectedContext()
  const workingDocumentSelection = getFileSelection(WORKING_DOCUMENT_ID)
  const workingDocumentInitialSelection =
    workingDocumentSelection && workingDocumentSelection.sections !== null
      ? workingDocumentSelection.sections
      : undefined
  const selectAll = useSelectAll({
    files,
  })

  return (
    <div className={classes.container}>
      <Dropzone>
        <PageHeader
          title={t('files.heading')}
          actions={
            <FileUploadMenu
              triggerButton={
                <Button
                  appearance="subtle"
                  icon={{ children: <AddRegular />, style: { color: 'inherit' } }}
                  className={classes.addButton}>
                  {t('addAction')}
                </Button>
              }
              openBraveSearch={() => setIsBraveOverlayOpen(true)}
            />
          }
        />
        <HierarchyViewPills value={hierarchyViewMode} onChange={setHierarchyViewMode} />
        <div className={classes.pageContent}>
          <div className={classes.workingDocument}>
            <FileCard
              fileId={WORKING_DOCUMENT_ID}
              fileName={t('files.workingDocumentFileName')}
              fileType={'docx'}
              sections={workingDocumentHierarchicalStructure}
              isLoading={isWorkingDocumentLoading}
              isError={isWorkingDocumentError}
              initialSelectedSections={workingDocumentInitialSelection}
              isExpanded={expandedFileId === WORKING_DOCUMENT_ID}
              onExpandToggle={() =>
                setExpandedFileId((prev) =>
                  prev === WORKING_DOCUMENT_ID ? null : WORKING_DOCUMENT_ID
                )
              }
              onContextChange={(sections, fileName) => {
                return updateFileSelection({
                  id: WORKING_DOCUMENT_ID,
                  fileName,
                  extension: 'docx',
                  sections,
                })
              }}
              hierarchyViewMode={hierarchyViewMode}
            />
          </div>
          {files && files.length > 0 && (
            <div className={classes.selectAllRow}>
              <Checkbox
                checked={selectAll.checked}
                onChange={selectAll.onChange}
                label={t('files.selectAll')}
                aria-label={t('files.selectAll')}
              />
            </div>
          )}
          <section className={classes.filesListSection}>
            <Body1Strong className="sr-only" as="h3">
              {t('files.uploadedFilesHeading')}
            </Body1Strong>
            <UploadingFilesList />
            {isLoading ? (
              <Spinner
                size="small"
                label={t('loading')}
                labelPosition="below"
                className={classes.spinner}
              />
            ) : (
              files?.map((file) => (
                <FileCard
                  key={file.id}
                  fileId={file.id}
                  fileName={file.name}
                  fileType={file.type}
                  isFileUploading={file.parsingStatus !== 'completed'}
                  isExcluded={file.isExcluded}
                  expiresAt={file.expiresAt}
                  initialSelectedSections={getFileSelection(file.id)?.sections ?? undefined}
                  isExpanded={expandedFileId === file.id}
                  onExpandToggle={() =>
                    setExpandedFileId((prev) => (prev === file.id ? null : file.id))
                  }
                  onContextChange={(sections, fileName) =>
                    updateFileSelection({
                      id: file.id,
                      fileName,
                      extension: file.type,
                      sections,
                    })
                  }
                  hierarchyViewMode={hierarchyViewMode}
                />
              ))
            )}
            {isError && (
              <Body1 as="p" role="alert">
                {t('genericError')}
              </Body1>
            )}
          </section>
        </div>
      </Dropzone>

      <Overlay
        title={t('brave.addBraveFiles')}
        content={<BraveUploadContent closeDrawer={() => setIsBraveOverlayOpen(false)} />}
        position="bottom"
        isOpen={isBraveOverlayOpen}
        size={95}
        setOpen={setIsBraveOverlayOpen}
      />
    </div>
  )
}

export { Files }
